import React, { useState } from "react";
import { useApi } from "src/Api/useApi";
import { Progress, Button, Input, Modal, Table } from "antd";
import styles from "./NeurapulseConfigDialog.module.css";
import clsx from "clsx";
import { Client, INeurapulseImportProgress } from "src/@types/ImpiricaPlatformApi";
import NeurapulseDuplicateClientsDialog from "./NeurapulseDuplicateClientsDialog";
import { toast } from "react-toastify";

interface NeurapulseClientImportProps {
	datasetId: number;
	organizationId: number;
	onRefreshComplete?: () => void;
}

export const NeurapulseClientImport: React.FC<NeurapulseClientImportProps> = ({
	datasetId,
	organizationId,
	onRefreshComplete,
}) => {
	const { streamingClient, neurapulseClient } = useApi();

	const [showImportProgress, setShowImportProgress] = useState<boolean>(false);
	const [percentComplete, setPercentComplete] = useState<number>(0);
	const [importError, setImportError] = useState<boolean>(false);
	const [searchEmails, setSearchEmails] = useState<string>("");
	const [checkingDuplicates, setCheckingDuplicates] = useState<boolean>(false);
	const [duplicatesFound, setDuplicatesFound] = useState<Client[]>([]);
	const [showDuplicatesDialog, setShowDuplicatesDialog] = useState<boolean>(false);
	const [failedImports, setFailedImports] = useState<INeurapulseImportProgress[]>([]);
	const [showFailedImportsModal, setShowFailedImportsModal] = useState<boolean>(false);

	const importByEmails = async () => {
		if (!streamingClient) return false;
		setShowImportProgress(true);
		setImportError(false);
		setFailedImports([]);
		try {
			const emailList = searchEmails
				.split(",")
				.map((email) => email.trim())
				.filter((email) => email.length > 0);
			if (emailList.length === 0) {
				toast.error("Please enter at least one valid email address.", {
					autoClose: 10000,
				});
				setImportError(true);
				return false;
			}

			// Email structure is validated server-side; invalid entries are
			// returned as per-email failures in the import progress stream.
			const apiEndpoint = `/clientapi/Neurapulse/${organizationId}/${datasetId}/import-test-takers-by-email`;
			const failures: INeurapulseImportProgress[] = [];
			for await (const progress of streamingClient.streamApiResponseForObjects<INeurapulseImportProgress>(
				apiEndpoint,
				"POST",
				{ emailAddresses: emailList }
			)) {
				setPercentComplete(progress.percentageComplete);
				if (!progress.success && progress.errorMessage) {
					failures.push(progress);
				}
			}

			setPercentComplete(100);
			if (failures.length > 0) {
				setFailedImports(failures);
				setShowFailedImportsModal(true);
				return false;
			}
		} catch (error) {
			console.error("Error during Neurapulse client data import by email:");
			setImportError(true);
			setShowImportProgress(false);
			toast.error("An error occurred during import. Please try again.", {
				autoClose: false,
			});
			return false;
		} finally {
			setShowImportProgress(false);
			setPercentComplete(0);
		}

		toast.success("Import completed successfully.");
		return true;
	};

	const refreshParticipants = async () => {
		if (onRefreshComplete) {
			onRefreshComplete();
			setSearchEmails("");
			setShowImportProgress(false);
			setPercentComplete(0);
			setImportError(false);
			setCheckingDuplicates(false);
			setDuplicatesFound([]);
			setShowDuplicatesDialog(false);
			setFailedImports([]);
			setShowFailedImportsModal(false);
		}
	};

	const importDataOnClick = async () => {
		const success = await importByEmails();
		const dupes = await checkForDuplicates();

		if (dupes && dupes.length > 0) {
			setDuplicatesFound(dupes);
			setShowDuplicatesDialog(dupes.length > 0);
		} else {
			if (success) {
				await refreshParticipants();
			}
		}
	};

	const checkForDuplicates = async () => {
		if (!neurapulseClient) return [];
		setCheckingDuplicates(true);
		try {
			const duplicates = await neurapulseClient.getDuplicateUnprocessedClients(
				organizationId,
				datasetId
			);
			return duplicates;
		} catch (error) {
			console.error("Error checking for duplicate test takers:");
			return [];
		} finally {
			setCheckingDuplicates(false);
		}
	};

	return (
		<div>
			<div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
				<Button
					onClick={importDataOnClick}
					disabled={showImportProgress || checkingDuplicates || !searchEmails.trim()}
					type="primary"
					className={clsx(styles.testButton)}
				>
					Import Test Takers
				</Button>
				{!showImportProgress && (
					<div style={{ width: "100%" }}>
						<Input.TextArea
							placeholder="Enter email addresses, separated by commas"
							value={searchEmails}
							onChange={(e) => setSearchEmails(e.target.value)}
							style={{ width: "100%", marginRight: 8 }}
						/>
					</div>
				)}
				{(showImportProgress || percentComplete === 100) && (
					<div style={{ width: "100%" }}>
						<Progress
							percent={percentComplete}
							status={importError ? "exception" : "active"}
						/>
					</div>
				)}
			</div>
			{checkingDuplicates && <div>Checking for duplicate test takers...</div>}
			{duplicatesFound.length > 0 && (
				<NeurapulseDuplicateClientsDialog
					organizationId={organizationId}
					datasetId={datasetId}
					clients={duplicatesFound}
					visible={showDuplicatesDialog}
					onClose={async (allConflictsResolved?: boolean) => {
						setShowDuplicatesDialog(false);
						if (allConflictsResolved) {
							await refreshParticipants();
						}
					}}
				/>
			)}
			<Modal
				title="Import Completed With Errors"
				visible={showFailedImportsModal}
				onOk={async () => {
					setShowFailedImportsModal(false);
					await refreshParticipants();
				}}
				onCancel={async () => {
					setShowFailedImportsModal(false);
					await refreshParticipants();
				}}
				cancelButtonProps={{ style: { display: "none" } }}
				width={840}
			>
				<p>The following test takers could not be imported:</p>
				<Table
					dataSource={failedImports}
					rowKey={(r) => r.email || r.firstName || Math.random().toString()}
					pagination={{ pageSize: 5 }}
					size="small"
					columns={[
						{ title: "Email", dataIndex: "email", key: "email" },
						{
							title: "Name",
							key: "name",
							render: (_: any, r: INeurapulseImportProgress) =>
								[r.firstName, r.lastName].filter(Boolean).join(" ") || "—",
						},
						{ title: "Error", dataIndex: "errorMessage", key: "errorMessage" },
					]}
				/>
			</Modal>
		</div>
	);
};

export default NeurapulseClientImport;
